import type { BuyerSubmissionPresentationDto } from "@/api/buyers";
import { MAX_PRESENT_PER_ACTION } from "@/lib/buyerSubmissionBulkRules";
import { buyerLabel } from "../buyerFilters";

/**
 * The Associated listings tab's sentences for presenting — the row's present
 * control, the "Presented" marker, the tick column, the bulk bar, its confirm
 * and its result.
 *
 * <p>The cap in these sentences is read from the shared rules module, never
 * typed, so the bar cannot say one number while the server refuses at another.
 * Tests import these rather than retyping them.</p>
 */
export const associationsCopy = {
  // ---- The row ------------------------------------------------------------
  presentThisOne: "Present this one",
  /** R3/D16 — beside the listing name, on a row whose buyer already has a
   *  standing submission for it (archived included). */
  presented: "Presented",
  /** The disabled control a presented row shows in PRESENT THIS ONE's place. */
  presentedButton: "Presented",
  alreadyPresented: "Already presented",

  // ---- Tick column (D13) --------------------------------------------------
  selectAllOnPage: "Select all on this page",
  selectAriaLabel: (listing: string) => `Select ${listing} to present`,
  presentedAriaLabel: (listing: string) => `${listing}: already presented`,

  // ---- Bulk bar -----------------------------------------------------------
  selectedCount: (n: number) => `${n} selected`,
  /** Appended when some ticks are on rows the reader cannot see right now —
   *  another page, or filtered out by the search. */
  notAllOnThisPage: "(some not on this page)",
  clear: "Clear",
  presentAllButton: (n: number) => `Present all selected (${n})`,
  tooMany: `Up to ${MAX_PRESENT_PER_ACTION} listings per action — clear some to continue.`,

  // ---- Confirm (D14) ------------------------------------------------------
  confirmTitle: (n: number) => (n === 1 ? "Present 1 listing?" : `Present ${n} listings?`),
  confirmIntro: "Each listing below gets its own submission for this buyer. Nothing is sent to anyone.",
  confirmSkippedHeading: "Left out",
  /** A tick whose row has left this buyer's list since it was ticked. */
  noLongerOnList: "No longer on this buyer's list",
  confirmLabel: "Present",

  // ---- Result (D15) -------------------------------------------------------
  resultSummary: (presented: number, skipped: number, failed: number) =>
    `${presented} presented · ${skipped} skipped · ${failed} failed`,
  resultSkippedHeading: "Skipped",
  resultFailedHeading: "Failed",
  dismissResult: "Dismiss",
  failedFallback: "Couldn't be presented.",
  /** A skip the server explained with the standing submission it found. */
  skippedExisting: (existing: BuyerSubmissionPresentationDto) =>
    existing.isArchived
      ? "Already presented — archived"
      : `Already presented — ${buyerLabel(existing.status)}`,
  /** A failed RESPONSE is no proof nothing landed (a timeout after the
   *  commit), so the list is refreshed and the sentence claims no outcome. */
  requestFailed: (message: string) =>
    `The request failed (${message}). The list has been refreshed to show what was presented.`,
} as const;
